<script setup>
import { computed, onBeforeUnmount, ref, useTemplateRef } from 'vue';

import Icon from 'next/icon/Icon.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';

import { useMapGetter } from 'dashboard/composables/store';
import { useMessageContext } from '../../provider.js';
import { MESSAGE_TYPES } from '../../constants.js';

const { attachments, contentAttributes, messageType, sender, inboxId } =
  useMessageContext();
const inboxGetter = useMapGetter('inboxes/getInbox');

const popoverRef = useTemplateRef('popoverRef');
const isOpen = ref(false);
const isPinned = ref(false);

let hideTimer;

const normalizeAddresses = value => {
  const addresses = Array.isArray(value) ? value : value ? [value] : [];
  return [
    ...new Set(addresses.map(address => String(address).trim()).filter(Boolean)),
  ];
};

const email = computed(() => contentAttributes.value?.email ?? {});

// Mail sent from here stores no From header; it always leaves from the inbox
// address, and agent profiles carry no email.
const inboxEmail = computed(() =>
  messageType.value === MESSAGE_TYPES.OUTGOING
    ? String(inboxGetter.value?.(inboxId?.value)?.email || '').trim()
    : ''
);

const fromAddresses = computed(() => {
  const addresses = normalizeAddresses(email.value.from);
  const senderEmail = sender.value?.email?.trim() || inboxEmail.value;

  if (!addresses.length && senderEmail) addresses.push(senderEmail);

  const senderName = (
    sender.value?.availableName ||
    sender.value?.available_name ||
    sender.value?.name ||
    ''
  ).trim();
  if (
    senderName &&
    senderEmail &&
    addresses[0]?.toLowerCase() === senderEmail.toLowerCase()
  ) {
    addresses[0] = `${senderName} <${addresses[0]}>`;
  }

  return addresses;
});

const toAddresses = computed(() =>
  normalizeAddresses(email.value.to ?? contentAttributes.value?.toEmails)
);

const ccAddresses = computed(() =>
  normalizeAddresses(email.value.cc ?? contentAttributes.value?.ccEmails)
);

const bccAddresses = computed(() =>
  normalizeAddresses(email.value.bcc ?? contentAttributes.value?.bccEmails)
);

const subject = computed(() => String(email.value.subject ?? '').trim());

const attachmentCount = computed(() => {
  return Number(
    email.value.numberOfAttachments ?? attachments.value?.length ?? 0
  );
});

const popoverAlign = computed(() =>
  messageType.value === MESSAGE_TYPES.OUTGOING ? 'end' : 'start'
);

const cancelHide = () => {
  clearTimeout(hideTimer);
};

const show = () => {
  cancelHide();
  popoverRef.value?.show();
};

const scheduleHide = () => {
  cancelHide();
  hideTimer = setTimeout(() => {
    if (!isPinned.value) popoverRef.value?.hide();
  }, 150);
};

const togglePinned = () => {
  isPinned.value = !isPinned.value;
  if (isPinned.value) show();
  else popoverRef.value?.hide();
};

const onShow = () => {
  isOpen.value = true;
};

const onHide = () => {
  isOpen.value = false;
  isPinned.value = false;
};

onBeforeUnmount(cancelHide);
</script>

<template>
  <Popover
    ref="popoverRef"
    :align="popoverAlign"
    @show="onShow"
    @hide="onHide"
  >
    <button
      type="button"
      class="inline-flex items-center justify-center size-5 p-0 border-0 rounded-md bg-transparent text-n-slate-10 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-1 focus-visible:outline-n-brand"
      :aria-label="$t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
      :aria-expanded="isOpen"
      @mouseenter="show"
      @mouseleave="scheduleHide"
      @focus="show"
      @blur="scheduleHide"
      @click.stop="togglePinned"
    >
      <Icon icon="i-lucide-circle-alert" class="size-3.5" />
    </button>

    <template #content>
      <dl
        data-email-details-popover
        class="grid grid-cols-[auto,minmax(0,1fr)] gap-x-3 gap-y-2 w-[min(24rem,calc(100vw-2rem))] max-h-80 p-4 text-xs text-n-slate-12"
        @mouseenter="cancelHide"
        @mouseleave="scheduleHide"
      >
        <dt class="text-n-slate-11">{{ $t('EMAIL_HEADER.FROM') }}</dt>
        <dd class="min-w-0 space-y-1">
          <div
            v-for="address in fromAddresses"
            :key="address"
            class="break-all"
          >
            {{ address }}
          </div>
          <span v-if="!fromAddresses.length">—</span>
        </dd>

        <dt class="text-n-slate-11">{{ $t('EMAIL_HEADER.TO') }}</dt>
        <dd class="min-w-0 space-y-1">
          <div
            v-for="address in toAddresses"
            :key="address"
            class="break-all"
          >
            {{ address }}
          </div>
          <span v-if="!toAddresses.length">—</span>
        </dd>

        <dt class="text-n-slate-11">{{ $t('EMAIL_HEADER.CC') }}</dt>
        <dd class="min-w-0 space-y-1">
          <div
            v-for="address in ccAddresses"
            :key="address"
            class="break-all"
          >
            {{ address }}
          </div>
          <span v-if="!ccAddresses.length">—</span>
        </dd>

        <template v-if="bccAddresses.length">
          <dt class="text-n-slate-11">{{ $t('EMAIL_HEADER.BCC') }}</dt>
          <dd class="min-w-0 space-y-1">
            <div
              v-for="address in bccAddresses"
              :key="address"
              class="break-all"
            >
              {{ address }}
            </div>
          </dd>
        </template>

        <template v-if="subject">
          <dt class="text-n-slate-11">{{ $t('EMAIL_HEADER.SUBJECT') }}</dt>
          <dd class="min-w-0 break-words">{{ subject }}</dd>
        </template>

        <template v-if="attachmentCount">
          <dt class="flex items-center text-n-slate-11">
            <Icon icon="i-lucide-paperclip" class="size-3.5" />
          </dt>
          <dd class="min-w-0">{{ attachmentCount }}</dd>
        </template>
      </dl>
    </template>
  </Popover>
</template>
